import React from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowForward,
    CheckCircleOutline,
    Code,
    HubOutlined,
    Terminal,
} from '@mui/icons-material';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import Seo from '../components/Seo';

const learningPaths = [
    {
        path: 'linux',
        icon: Terminal,
        eyebrow: 'Start here',
        title: 'Linux foundations',
        copy: 'Get comfortable navigating the shell, working with files, and turning everyday commands into reliable habits.',
        skills: ['Files & navigation', 'Shell commands', 'Safe workflows'],
        accent: '#4f46e5',
        tint: '#eef2ff',
    },
    {
        path: 'networking',
        icon: HubOutlined,
        eyebrow: 'Build confidence',
        title: 'Networking practice',
        copy: 'Follow traffic from DNS to HTTP and learn a repeatable approach for finding where connections fail.',
        skills: ['DNS lookups', 'Routes & reachability', 'HTTP checks'],
        accent: '#0f766e',
        tint: '#ecfdf5',
    },
    {
        path: 'kubernetes',
        icon: Code,
        eyebrow: 'Go further',
        title: 'Kubernetes operations',
        copy: 'Work with pods, deployments, logs, and rollouts in labs shaped around real operational tasks.',
        skills: ['Workloads', 'Troubleshooting', 'Rollouts'],
        accent: '#b45309',
        tint: '#fffbeb',
    },
];

const HomePage = () => {
    const description = 'Practice Linux, networking, and Kubernetes in guided browser labs with real tasks and automatic checks.';

    return (
        <Box sx={{ display: 'grid', gap: { xs: 6, md: 9 }, pb: { xs: 3, md: 6 } }}>
            <Seo
                title="Hands-on Linux and DevOps Labs"
                description={description}
                path="/"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'EducationalOrganization',
                    name: 'NixDojo',
                    url: 'https://nixdojo.com',
                    description,
                    teaches: ['Linux', 'Networking', 'Kubernetes', 'DevOps']
                }}
            />

            <Box
                component="section"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1.05fr 0.95fr' },
                    gap: { xs: 5, lg: 8 },
                    alignItems: 'center',
                    pt: { xs: 3, md: 6 },
                }}
            >
                <Box>
                    <Chip label="Your hands-on DevOps practice ground" color="secondary" variant="outlined" sx={{ mb: 2.5 }} />
                    <Typography variant="h1" sx={{ maxWidth: 760, mb: 2.5, fontSize: { xs: '2.65rem', md: '4.25rem' }, letterSpacing: '-0.04em' }}>
                        Don&apos;t just learn Linux. <Box component="span" sx={{ color: 'primary.main' }}>Run it.</Box>
                    </Typography>
                    <Typography color="text.secondary" sx={{ maxWidth: 680, fontSize: { xs: '1.05rem', md: '1.2rem' }, mb: 4 }}>
                        Open a browser lab, work through a real task, and get immediate checks on your progress. Build the command-line instincts you need for Linux, networking, and Kubernetes.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <Button component={Link} to="/demo" variant="contained" size="large" endIcon={<ArrowForward />}>
                            Try a lab now
                        </Button>
                        <Button component={Link} to="/lesson" variant="outlined" size="large">
                            Browse learning paths
                        </Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} sx={{ mt: 3 }}>
                        {['No local setup', 'Learn at your pace', 'Practice with real commands'].map((item) => (
                            <Stack key={item} direction="row" spacing={0.75} alignItems="center">
                                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 18 }} />
                                <Typography variant="body2" color="text.secondary">{item}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                <Paper
                    variant="outlined"
                    sx={{
                        overflow: 'hidden',
                        borderColor: '#273449',
                        bgcolor: '#0b1120',
                        color: '#e2e8f0',
                        boxShadow: '0 30px 70px rgba(15, 23, 42, 0.24)',
                        transform: { lg: 'rotate(1deg)' },
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5, bgcolor: '#111827', borderBottom: '1px solid #273449' }}>
                        <Stack direction="row" spacing={0.8}>
                            {['#fb7185', '#fbbf24', '#34d399'].map((color) => <Box key={color} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />)}
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>nixdojo-lab — bash</Typography>
                        <Box sx={{ width: 45 }} />
                    </Stack>
                    <Box sx={{ p: { xs: 2.5, md: 4 }, fontFamily: 'monospace', fontSize: { xs: '0.82rem', sm: '0.95rem' }, minHeight: { xs: 300, md: 380 } }}>
                        <Typography component="div" sx={{ font: 'inherit', color: '#64748b', mb: 2 }}># Task: find the unhealthy workload</Typography>
                        <Typography component="div" sx={{ font: 'inherit', mb: 1 }}><Box component="span" sx={{ color: '#34d399' }}>learner@nixdojo</Box>:~$ kubectl get pods</Typography>
                        <Typography component="pre" sx={{ font: 'inherit', m: 0, color: '#cbd5e1', whiteSpace: 'pre-wrap' }}>{'NAME              READY   STATUS             RESTARTS\nweb-7d9c8b-x2k4p   0/1     CrashLoopBackOff   3'}</Typography>
                        <Typography component="div" sx={{ font: 'inherit', mt: 2, mb: 1 }}><Box component="span" sx={{ color: '#34d399' }}>learner@nixdojo</Box>:~$ kubectl logs web-7d9c8b-x2k4p</Typography>
                        <Typography component="div" sx={{ font: 'inherit', color: '#fda4af' }}>error: CONFIG_PATH is not set</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3, p: 1.5, border: '1px solid #14532d', bgcolor: 'rgba(20, 83, 45, 0.28)', borderRadius: 1 }}>
                            <CheckCircleOutline sx={{ color: '#4ade80', fontSize: 20 }} />
                            <Typography sx={{ font: 'inherit', color: '#bbf7d0' }}>Check passed — root cause identified</Typography>
                        </Stack>
                        <Typography component="div" sx={{ font: 'inherit', mt: 2 }}><Box component="span" sx={{ color: '#34d399' }}>learner@nixdojo</Box>:~$ <Box component="span" sx={{ display: 'inline-block', width: 8, height: 18, bgcolor: '#e2e8f0', verticalAlign: 'middle', animation: 'blink 1.1s step-end infinite', '@keyframes blink': { '50%': { opacity: 0 } } }} /></Typography>
                    </Box>
                </Paper>
            </Box>

            <Box component="section" aria-labelledby="paths-heading">
                <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 4 }}>
                    <Typography color="primary.main" fontWeight={700} sx={{ mb: 1, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.78rem' }}>Choose your next challenge</Typography>
                    <Typography id="paths-heading" variant="h2" sx={{ mb: 1.5 }}>Start with the skills you need.</Typography>
                    <Typography color="text.secondary">Pick a path, read the goal, then do the work yourself in a dedicated lab environment.</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                    {learningPaths.map(({ path, icon: Icon, eyebrow, title, copy, skills, accent, tint }) => (
                        <Paper key={title} variant="outlined" sx={{ p: { xs: 3, md: 3.5 }, display: 'flex', flexDirection: 'column', minHeight: 330, transition: 'transform .2s ease, box-shadow .2s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.09)' } }}>
                            <Box sx={{ width: 48, height: 48, display: 'grid', placeItems: 'center', bgcolor: tint, color: accent, borderRadius: 1.5, mb: 3 }}><Icon /></Box>
                            <Typography variant="caption" sx={{ color: accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{eyebrow}</Typography>
                            <Typography variant="h4" sx={{ mt: 0.75, mb: 1.5 }}>{title}</Typography>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>{copy}</Typography>
                            <Stack spacing={0.8} sx={{ mb: 3 }}>
                                {skills.map((skill) => <Typography key={skill} variant="body2" sx={{ '&::before': { content: '"→"', color: accent, mr: 1 } }}>{skill}</Typography>)}
                            </Stack>
                            <Button component={Link} to={`/lesson?path=${path}`} sx={{ mt: 'auto', alignSelf: 'flex-start', color: accent, px: 0 }} endIcon={<ArrowForward />}>Explore this path</Button>
                        </Paper>
                    ))}
                </Box>
            </Box>

            <Paper component="section" variant="outlined" sx={{ p: { xs: 3, md: 5 }, bgcolor: '#111827', color: 'white', borderColor: '#273449' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
                    <Box sx={{ maxWidth: 720 }}>
                        <Typography variant="h3" sx={{ mb: 1 }}>The terminal is where it starts to stick.</Typography>
                        <Typography sx={{ color: '#cbd5e1' }}>Try the guided shell demo without an account, then move into full labs when you are ready.</Typography>
                    </Box>
                    <Button component={Link} to="/demo" variant="contained" size="large" endIcon={<ArrowForward />} sx={{ whiteSpace: 'nowrap' }}>Open free demo</Button>
                </Stack>
            </Paper>

            <Alert severity="info" variant="outlined">
                NixDojo is under active development. Labs and account workflows will continue to improve as the platform grows.
            </Alert>
        </Box>
    );
};

export default HomePage;
